<template>
    <div>
        <div class="alert alert-warning" role="alert" v-if="isNegativeBundle">
            <span>
                <i class="cus-error"></i>
                Bundle total can not be less then 0.00 !
            </span>
        </div>
        <div class="form-group form-row">
            <label for="item" class="col-md-3 col-form-label">Item:</label>
            <div class="col-md-9">
                <vet-select
                    id="item"
                    module="bundle"
                    placeholder="Item (required)"
                    v-model="form.item"
                    name="item"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('item') }"
                />
            </div>
        </div>
        <div class="form-group form-row">
            <label for="quantity" class="col-md-3 col-form-label"
                >Quantity:</label
            >
            <div class="col-md-9">
                <div class="form-row">
                    <div class="col-md-9">
                        <input
                            type="number"
                            class="form-control"
                            name="quantity"
                            id="quantity"
                            v-model="form.quantity"
                            placeholder="Quantity (required)"
                            v-validate="
                                'required|min_value:0|numericalWithDecimal|decimal:3'
                            "
                            :class="{
                                'border-danger': errors.has('quantity')
                            }"
                            @input="calculateTotalPrice"
                        />
                    </div>
                    <div class="col-md-3 d-flex align-items-center">
                        <span v-if="form.item">{{
                            form.item.uom ? form.item.uom : "UOM"
                        }}</span>
                        <span v-else>{{ "UOM" }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-group form-row">
            <label for="price-option" class="col-md-3 col-form-label"
                >Price:</label
            >
            <div class="col-md-9">
                <select
                    class="form-control"
                    id="price-option"
                    v-model="form.isCustomPrice"
                    @change="selectIsCustomPrice"
                >
                    <option value="0">Default price from Inventory</option>
                    <option value="1">Custom price</option>
                </select>
            </div>
        </div>
        <div v-if="isUsingCustomPrice" class="form-group form-row">
            <label for="unit-price" class="col-md-3 col-form-label"
                >Unit Price:</label
            >
            <div class="col-md-9">
                <input
                    :disabled="!isAllowEditPrice"
                    type="text"
                    class="form-control"
                    id="unit-price"
                    v-model="form.unitPrice"
                    placeholder="Unit Price (required)"
                    name="unitPrice"
                    v-validate="'required|numericalWithDecimal|decimal:2'"
                    :class="{
                        'border-danger': errors.has('unitPrice')
                    }"
                    @input="calculateTotalPrice"
                />
            </div>
        </div>
        <div v-if="isUsingCustomPrice" class="form-group form-row">
            <label for="fees" class="col-md-3 col-form-label">Fees:</label>
            <div class="col-md-9">
                <vet-select-fee
                    v-if="form.item"
                    :value="form.fees"
                    @input="fees => selectFees(fees)"
                    :item-id="form.item.id"
                ></vet-select-fee>
            </div>
        </div>

        <div class="form-group form-row">
            <label for="print-invoice" class="col-md-3 col-form-label"
                >Print on Invoice:</label
            >
            <div class="col-md-9">
                <select
                    class="form-control"
                    id="print-invoice"
                    v-model="form.printOnInvoice"
                    @change="selectIsPrintOnInvoice"
                >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                </select>
            </div>
        </div>
        <div class="form-group form-row" v-if="!isEdit">
            <label for="actions" class="col-md-3 col-form-label"
                >Actions:</label
            >
            <div class="col-md-9">
                <v-select
                    class="show-actions"
                    v-model="form.actions"
                    multiple
                    name="actions"
                    :label="'label'"
                    placeholder="Actions (optional)"
                    :options="actionOptions"
                >
                </v-select>
            </div>
        </div>
        <div class="form-group form-row">
            <label for="totalPrice" class="col-md-3 col-form-label"
                >Total:</label
            >
            <div class="col-md-9">
                <input
                    :disabled="!isAllowEditTotalPrice"
                    v-model="form.totalPrice"
                    type="text"
                    class="form-control"
                    id="totalPrice"
                    name="totalPrice"
                    placeholder="Total (required)"
                    v-validate="'required|numericalWithDecimal'"
                    :class="{
                        'border-danger': errors.has('totalPrice')
                    }"
                    @input="calculateUnitPrice"
                />
            </div>
        </div>
        <div class="form-group form-row" v-if="!isEdit">
            <label for="add-activity" class="col-md-3 col-form-label"
                >Add As Activity:</label
            >
            <div class="col-md-9">
                <select
                    v-model="form.addAsActivity"
                    id="add-activity"
                    class="form-control"
                >
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                </select>
            </div>
        </div>
    </div>
</template>

<script>
import VetSelect from "@/module/commons/vet-select";
import vSelect from "vue-select";
import vetSelectFee from "@/module/commons/vet-select-fee";
import ItemBundleServices from "@/services/items/items-bundle";

import { isNaN, map, debounce } from "lodash";

export default {
    components: {
        VetSelect,
        vSelect,
        vetSelectFee
    },
    props: {
        formData: {
            type: Object
        },
        isEdit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isFirstLoadOnEdit: false,
            isNegativeBundle: false,
            loading: {
                item: false,
                total: false,
                negative: false
            },
            oldData: {
                itemID: null,
                quantity: 0,
                fees: [],
                feesTotal: 0
            },
            form: {
                item: null,
                id: null,
                quantity: 0,
                unitPrice: 0,
                totalPrice: 0,
                printOnInvoice: 1,
                isCustomPrice: 0,
                fees: [],
                feesTotal: 0,
                actions: [],
                addAsActivity: "0",
                ...this.formData
            }
        };
    },
    mounted() {
        this.form.unitPrice = parseFloat(this.form.unitPrice).toFixed(2);
        this.form.totalPrice = parseFloat(this.form.totalPrice).toFixed(2);
    },
    watch: {
        form: {
            handler(val) {
                this.$emit("update-form", val);
            },
            deep: true
        },
        "form.item": async function (value) {
            if (!this.form.item) {
                return;
            }
            if (this.oldData.itemID === value.id) {
                return;
            }
            this.form.item.id = value ? value.id : null;
            await this.getItemDetails();
            this.changedItem();
        },
        addAsActivity() {
            this.$emit("add-activity", this.isAddAsActivity);
        },
        isSaveDisabled(value) {
            this.$emit("is-save-disabled", value);
        }
    },
    computed: {
        templateId() {
            return this.$store.state.settingsTemplate.details.id;
        },
        isUsingCustomPrice() {
            return this.form.isCustomPrice == "1";
        },
        isPrintOnInvoice() {
            return this.form.printOnInvoice == "1";
        },
        isZeroQuantity() {
            return this.form.quantity == 0;
        },
        isAllowEditPrice() {
            return this.isPrintOnInvoice && this.isUsingCustomPrice;
        },
        isAllowEditTotalPrice() {
            return (
                this.isPrintOnInvoice &&
                this.isUsingCustomPrice &&
                !this.isZeroQuantity
            );
        },
        isAddAsActivity() {
            return this.form.addAsActivity === "1";
        },
        actionOptions() {
            if (!this.form.item) {
                return [];
            }

            return map(this.form.item.actions, (action, id) => {
                return {
                    id: id,
                    label: action
                };
            });
        },
        isSaveDisabled() {
            return (
                this.loading.item ||
                this.loading.total ||
                this.loading.negative ||
                this.isNegativeBundle
            );
        }
    },
    methods: {
        reset() {
            // customPrice is set outside of nextTick to prevent vee-validate
            // validate field that not rendered yet by customPrice.
            this.form.isCustomPrice = 0;

            this.$nextTick(() => {
                this.form.item = null;
                this.form.quantity = 0;
                this.form.unitPrice = (0).toFixed(2);
                this.form.totalPrice = (0).toFixed(2);
                this.form.printOnInvoice = 1;
                this.form.fees = [];
                this.form.feesTotal = (0).toFixed(2);
                this.form.actions = [];
                this.form.addAsActivity = "0";

                this.oldData.itemID = null;
                this.oldData.quantity = 0;
                this.oldData.fees = [];
                this.oldData.feesTotal = 0;

                this.$validator.reset();
            });
        },
        selectFees(fees) {
            this.form.fees = fees;
            this.calculateTotalPrice();
        },
        selectIsPrintOnInvoice() {
            if (this.isPrintOnInvoice) {
                this.resetDataCalculation(false);
            }
            this.calculateTotalPrice();
        },
        async selectIsCustomPrice() {
            let self = this;
            let isResetFees = true;

            if (this.isEdit) {
                if (self.form.isCustomPrice == 0) {
                    await self.getItemDetails(true);
                }
                if (self.form.isCustomPrice == 1) {
                    // self.form.quantity = self.oldData.quantity;
                    self.form.fees = self.oldData.fees;
                    self.form.feesTotal = self.oldData.feesTotal;
                    isResetFees = false;
                }

                if (this.isFirstLoadOnEdit) {
                    this.isFirstLoadOnEdit = false;
                }
            }
            self.resetDataCalculation(isResetFees);
            self.calculateTotalPrice();
        },
        async getTotalPrice() {
            try {
                this.loading.total = true;

                const res = await ItemBundleServices.getTotalPrice(
                    this.form.item.id,
                    {
                        quantity: this.form.quantity || 0,
                        unit_price: this.form.unitPrice || 0,
                        is_custom_price: this.form.isCustomPrice,
                        fees: map(this.form.fees, fee => fee.id)
                    }
                );
                if (res.message && res.message.code == 200) {
                    this.form.feesTotal = res.resource.fee_price;
                    this.loading.total = false;

                    return res.resource.total_price;
                } else {
                    throw true;
                }
            } catch (error) {
                console.error("Error when calculate total price");
                this._message("Error when calculate total price", "danger");
            }
        },
        async checkNegativeBundle() {
            if (!this.form.item || !this.form.totalPrice) {
                return;
            }
            try {
                this.loading.negative = true;

                this.isNegativeBundle = false;
                const res = await ItemBundleServices.checkNegativeBundle(
                    this.form.item.id,
                    {
                        template_id: this.templateId,
                        element_total: this.form.totalPrice
                    }
                );
                if (res.message && res.message.code == 200) {
                    this.isNegativeBundle = res.resource.negative_bundle;
                    this.loading.negative = false;
                    return this.isNegativeBundle;
                } else {
                    throw true;
                }
            } catch (error) {
                console.error("Error when check negative bundle");
                this._message("Error when check negative bundle", "danger");
                return true;
            }
        },
        calculateUnitPrice() {
            this.form.unitPrice = (
                (this.form.totalPrice - this.form.feesTotal) /
                this.form.quantity
            ).toFixed(2);
            this.checkNegativeBundle();
        },
        calculateTotalPrice: debounce(async function () {
            this.isNegativeBundle = false;
            if (
                !this.form.item ||
                isNaN(this.form.quantity) ||
                isNaN(this.form.unitPrice)
            ) {
                return;
            }

            // if edit and first load, don't calculate total price as it already set by user
            if (
                this.isEdit &&
                this.isFirstLoadOnEdit &&
                this.isUsingCustomPrice
            ) {
                this.isFirstLoadOnEdit = false;
                return;
            }

            let total = 0;

            if (this.isPrintOnInvoice) {
                total = await this.getTotalPrice();
            } else {
                this.form.unitPrice = total.toFixed(2);
            }

            this.form.totalPrice = total.toFixed(2);
            this.checkNegativeBundle();
        }, 500),
        resetDataCalculation(changeFee = true) {
            if (!this.form.item) {
                return;
            }

            // this.form.item has different structure when fetching from item list & new item form
            if (this.isUsingCustomPrice) {
                this.form.unitPrice =
                    this.form.item.unit_price ||
                    (this.form.item.price / this.form.item.dose).toFixed(2);
            } else {
                this.form.unitPrice = (
                    this.form.item.price / this.form.item.dose
                ).toFixed(2);
            }

            if (changeFee) {
                this.form.feesTotal = this.form.item.fees_total;
                this.form.fees = map(this.form.item.fees, fee => {
                    return {
                        id: fee.id,
                        name: fee.label,
                        encoded_id: fee.encoded_id
                    };
                });
            }
        },
        async changedItem() {
            if (!this.form.item) {
                return;
            }

            let item;
            this.form.fees = [];

            item = this.form.item;

            this.form.fees = map(item.fees, fee => {
                return {
                    id: fee.id,
                    name: fee.label,
                    encoded_id: fee.encoded_id
                };
            });
            this.form.feesTotal = item.fees_total;

            this.form.actions = this.actionOptions;

            this.form.printOnInvoice = item.is_print_on_invoice === "1" ? 1 : 0;
            this.form.unitPrice = (item.price / item.dose).toFixed(2);

            this.calculateTotalPrice();
        },
        async getItemDetails(skipOldData = false) {
            if (!this.form.item) {
                return;
            }

            try {
                this.loading.item = true;

                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}/inventory/${this.form.item.id}`
                );

                if (
                    res.data &&
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code == 200
                ) {
                    if (!this.form.item) {
                        this.loading.item = false;
                        return;
                    }

                    this.form.item.details = res.data.response.resources;

                    //set old default value
                    if (!skipOldData) {
                        this.oldData = {
                            quantity: this.form.quantity,
                            fees: this.form.fees,
                            feesTotal: this.form.item.fees_total
                        };
                    }

                    this.form.quantity = this.isEdit
                        ? this.form.quantity
                        : this.form.item.details.dose;
                    this.form.item.fees = this.form.item.details.fees;
                    this.form.item.fees_total = this.form.item.details.fees_total;

                    this.loading.item = false;
                } else {
                    throw true;
                }
            } catch (error) {
                console.error(error);
                this._message("Error when fetching item detail", "danger");
            }
        },
        async save() {
            let res = {};

            try {
                this.isLoading = true;

                res = await this.$http.post(
                    `${process.env.BARRAMUNDI}account/template/bundle/add-item/${this.templateId}`,
                    {
                        id: this.isEdit ? this.form.id : null,
                        itemid: this.form.item.id,
                        quantity: this.form.quantity,
                        unit_price: this.form.unitPrice,
                        total_price: this.form.totalPrice,
                        print_on_invoice: this.form.printOnInvoice,
                        price_option: this.form.isCustomPrice,
                        fees_total: this.form.feesTotal,
                        fees: this.form.fees.map(fee => fee.id),
                        iitem_actions: this.isEdit
                            ? []
                            : this.form.actions.map(action => action.id)
                    }
                );

                if (res.data.response && res.data.response.code != 200) {
                    throw new Error(res.data.response.message);
                }

                this._message(res.data.response.message);
            } catch (error) {
                console.log(error.message);
                this._message("Error saving data", "danger");
            } finally {
                this.isLoading = false;
            }
        },
        mergeData(data) {
            let self = this;

            this.$nextTick(() => {
                self.form = data;
                // if (self.form.isCustomPrice == 0) {
                self.oldData = {
                    itemID: self.form.item.id,
                    quantity: self.form.quantity,
                    fees: self.form.fees,
                    feesTotal: self.form.feesTotal,
                    unitPrice: self.form.unitPrice
                };
                self.isFirstLoadOnEdit = true;
                // }
            });
        }
    }
};
</script>
